import { useEffect, useRef, useState } from 'react';
import axios, { Method } from 'axios';
import { useTranslation } from 'react-i18next';
import { PAL_EVENT_TYPE } from './PALEventTypes';
import {
  isPersonalMessage,
  PersonalMessage,
  PersonalMessageList,
  PersonalMessageState,
} from '../../../types/api/PAL/PersonalMessageList';
import { Namespace } from '../../../translation/namespace';
import Event from '../../../lib/EventEmitter';
import { useWebSockets } from '../../../_lib/WebSocketManager';
import { windowLogger } from '../../../_lib/Logger/windowLogger';

export type PersonalMessagesState = {
  messageCategory: string;
  items: PersonalMessage[];
};

// init default state received from PAL
const initDefaultNotificationState = (initialPALMessages: PersonalMessageList[]): PersonalMessagesState[] => {
  return initialPALMessages.map(x => ({
    items: x.items,
    messageCategory: x.messageCategory,
  }));
};

const createEventMap = (eventName: string, callbackfn: (...e: any[]) => void) => ({ eventName, callbackfn });

const addMessage = (e: PersonalMessage) => (prevItems: PersonalMessage[]) => [e, ...prevItems];
const updateMessage = (e: PersonalMessage) => (prevMessages: PersonalMessage[]) =>
  prevMessages.map((message: PersonalMessage) => (message?.id === e.id ? e : message));
const deleteMessage = (e: PersonalMessage) => (prevMessages: PersonalMessage[]) =>
  prevMessages.filter(message => message.id !== e.id);

/**
 * Handles all Websocket Messages inside the PAL.
 *
 * - Differentiating messages by messageCategory, not elementType, as multiple ElementType.pluginmessages are possible.
 * - Initialize the state of each messageCategory and messages from props
 *    -> no need to merge messages from props and state everytime
 *    -> easier to handle state while updating/deleting messages
 * - Keeping the state of all messages inside here, not in the message component itself, allows us to
 *    -> easily handle incoming messages of all categories in one place
 *    -> keep state consistent, event if certain Components that rely on this messages are not mounted!
 */
export const usePALMessages = (initialPALMessages: PersonalMessageList[]) => {
  const { t, i18n } = useTranslation();

  /**
   * Letting the screen reader know that a new notification is available.
   * If a message Category is unknown, we assume it is a custom tab.
   */
  const notifyLiveRegionOnNewMessage = (messageCategory: string) => {
    const keyByCategory = (category: string) => `${Namespace.PersonalAccessLibrary}:newNotification.${category}`;
    const message = i18n.exists(keyByCategory(messageCategory))
      ? keyByCategory(messageCategory)
      : keyByCategory('custom');

    Event.emit(Event.liveRegionMessage.sendLiveRegionMessage, t(message));
  };

  const [messagesStates, setMessagesStates] = useState<PersonalMessagesState[]>(
    initDefaultNotificationState(initialPALMessages),
  );
  const subscribe = useWebSockets();

  const updateMessages = (
    messageCategory: string,
    operation: (prevMessages: PersonalMessage[]) => PersonalMessage[],
  ) => {
    setMessagesStates(states =>
      states.map(state =>
        state.messageCategory === messageCategory
          ? {
              ...state,
              items: operation(state.items),
            }
          : state,
      ),
    );
  };

  /**
   * Listening to incoming CREATE messages.
   *
   * - add message to array.
   * - send live region notification that a message of certain type was added.
   */
  const handleCREATE = (messageCategory: string) => (e: PersonalMessage) => {
    if (isPersonalMessage(e)) {
      notifyLiveRegionOnNewMessage(messageCategory);
      updateMessages(messageCategory, addMessage(e));
    }
  };

  /**
   * Listening to incoming UPDATE messages.
   *
   * - update message array if some message has the same id.
   */
  const handleUPDATE = (messageCategory: string) => (e: PersonalMessage) => {
    if (isPersonalMessage(e)) {
      updateMessages(messageCategory, updateMessage(e));
    }
  };

  /**
   * Listening to incoming DELETE messages.
   * - update list with timeout so have have the chance to display the success messages
   */
  const handleDELETE = (messageCategory: string) => (e: PersonalMessage) => {
    if (isPersonalMessage(e)) {
      updateMessages(messageCategory, deleteMessage(e));
    }
  };

  /**
   * Creating WS connection for each category and the corresponding handler.
   * - useRef so eventMap will not be calculated everytime,
   *   -> else useSocketManager will disconnect and connect every render as its in useEffect!
   */
  const eventMap = useRef(
    initialPALMessages
      .map((current) => {
        const { messageCategory } = current;
        return [
          createEventMap(`${messageCategory}:${PAL_EVENT_TYPE.CREATE}`, handleCREATE(messageCategory)),
          createEventMap(`${messageCategory}:${PAL_EVENT_TYPE.UPDATE}`, handleUPDATE(messageCategory)),
          createEventMap(`${messageCategory}:${PAL_EVENT_TYPE.DELETE}`, handleDELETE(messageCategory)),
        ];
      })
      .flat(),
  );

  useEffect(() => {
    const subscriptions = eventMap.current.map(sub => subscribe(sub.eventName, sub.callbackfn));
    return () => subscriptions.forEach(unsubscribe => unsubscribe());
  }, [subscribe]);

  const getMessageCategory = (messageCategory?: string) => {
    return messagesStates.find(n => n.messageCategory === messageCategory);
  };

  const hasUnreadMessages = (messageCategory?: string) =>
    getMessageCategory(messageCategory)?.items?.some(message => message.messageState === PersonalMessageState.UNREAD) ??
    false;

  /**
   * This marks a certain Category as read by sending a POST.
   * -> if the POST fails, the 'bobble' will not be removed as no UPDATE message will be sent from backend.
   */
  const markMessageCategoryAsRead = (messageCategory?: string) => {
    const messageCategoryProp = initialPALMessages.find(n => n.messageCategory === messageCategory);

    if (messageCategoryProp?.readForm.method && hasUnreadMessages(messageCategory)) {
      axios
        .request({
          method: messageCategoryProp.readForm.method as Method,
          url: messageCategoryProp.readForm.url,
        })
        .catch(reason => windowLogger.error(`Request failed to '${messageCategoryProp.readForm.url}': ${reason}`));
    }
  };

  return { markMessageCategoryAsRead, hasUnreadMessages, messagesStates };
};
